
import './bootstrap-loader';
import { bootstrapOauthTraceFromUrl } from './app/services/oauth-trace.service';
bootstrapOauthTraceFromUrl();

import { bootstrapApplication } from '@angular/platform-browser';

import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';

// Aggressive logging suppression - runs before Angular starts
(function() {
  'use strict';
  
  // Store original methods
  const originalLog = console.log;
  const originalWarn = console.warn;
  const originalError = console.error;
  const originalInfo = console.info;
  const originalDebug = console.debug;
  const originalTrace = console.trace;
  const originalGroup = console.group;
  const originalGroupEnd = console.groupEnd;
  const originalTable = console.table;
  const originalTime = console.time;
  const originalTimeEnd = console.timeEnd;
  const originalCount = console.count;
  const originalAssert = console.assert;

  // Function to check if message should be suppressed
  function shouldSuppressMessage(message: any): boolean {
    if (typeof message !== 'string') {
      // Check if it's an array and any element contains suppressed patterns
      if (Array.isArray(message)) {
        return message.some(arg => typeof arg === 'string' && shouldSuppressMessage(arg));
      }
      return false;
    }
    
    const suppressedPatterns = [
      'Angular is running in development mode',
      'XHR finished loading',
      'XHR finished loading:',
      'Fetch finished loading',
      'Fetch finished loading:',
      'XMLHttpRequest',
      'fetch',
      'GET "',
      'POST "',
      'PUT "',
      'DELETE "',
      'PATCH "',
      '/api/',
      'techmicrosolutions.com',
      'innovnet.ai',
      'stripe.com',
      'm.stripe.com',
      'NG0913',
      'intrinsic file dimensions much larger than its rendered size',
      'Angular DevTools',
      'zone.js',
      'core.mjs',
      'platform-browser.mjs',
      'router.mjs',
      'Observable',
      'Subscriber',
      'Subject',
      'BehaviorSubject',
      'OperatorSubscriber',
      'mergeInternals',
      'switchMap',
      'map',
      'filter',
      'take',
      'tap',
      'catchError',
      'finalize',
      'takeUntil',
      'throwIfEmpty',
      'defer',
      'startWith',
      'combineLatest',
      'innerFrom',
      'lift',
      'doInnerSub',
      'outerNext',
      'errorContext',
      'handleNavigationRequest',
      'scheduleNavigation',
      'navigateToSyncWithBrowser',
      'initialNavigation',
      'loadComponent',
      'bootstrap',
      'internalCreateApplication',
      'bootstrapApplication',
      'onInvoke',
      'onScheduleTask',
      'onHasTask',
      'onLeave',
      'onInvokeTask',
      'scheduleTask',
      'runTask',
      'invokeTask',
      'drainMicroTaskQueue',
      'globalCallback',
      'globalZoneAwareCallback',
      'Zone - Promise.then',
      'Zone - setTimeout',
      'Zone - XMLHttpRequest.send'
    ];
    
    // Check if message contains any suppressed pattern
    const containsPattern = suppressedPatterns.some(pattern => message.includes(pattern));
    
    // Also check for URL patterns (API endpoints)
    const urlPattern = /https?:\/\/[^\s"]+/;
    if (urlPattern.test(message)) {
      return true; // Suppress any message containing URLs
    }
    
    return containsPattern;
  }

  // Override all console methods
  console.log = function(...args: any[]) {
    // Check all arguments, not just the first one
    if (args.some(arg => shouldSuppressMessage(arg))) return;
    (originalLog as any).apply(console, args);
  };

  console.warn = function(...args: any[]) {
    // Check all arguments, not just the first one
    if (args.some(arg => shouldSuppressMessage(arg))) return;
    (originalWarn as any).apply(console, args);
  };

  console.error = function(...args: any[]) {
    // Check all arguments, not just the first one
    if (args.some(arg => shouldSuppressMessage(arg))) return;
    (originalError as any).apply(console, args);
  };

  console.info = function(...args: any[]) {
    // Check all arguments, not just the first one
    if (args.some(arg => shouldSuppressMessage(arg))) return;
    (originalInfo as any).apply(console, args);
  };

  console.debug = function(...args: any[]) {
    // Check all arguments, not just the first one
    if (args.some(arg => shouldSuppressMessage(arg))) return;
    (originalDebug as any).apply(console, args);
  };

  console.trace = function(...args: any[]) {
    if (args.length > 0 && shouldSuppressMessage(args[0])) return;
    (originalTrace as any).apply(console, args);
  };

  console.group = function(...args: any[]) {
    if (args.length > 0 && shouldSuppressMessage(args[0])) return;
    (originalGroup as any).apply(console, args);
  };

  console.groupEnd = function(...args: any[]) {
    if (args.length > 0 && shouldSuppressMessage(args[0])) return;
    (originalGroupEnd as any).apply(console, args);
  };

  console.table = function(...args: any[]) {
    if (args.length > 0 && shouldSuppressMessage(args[0])) return;
    (originalTable as any).apply(console, args);
  };

  console.time = function(...args: any[]) {
    if (args.length > 0 && shouldSuppressMessage(args[0])) return;
    (originalTime as any).apply(console, args);
  };

  console.timeEnd = function(...args: any[]) {
    if (args.length > 0 && shouldSuppressMessage(args[0])) return;
    (originalTimeEnd as any).apply(console, args);
  };

  console.count = function(...args: any[]) {
    if (args.length > 0 && shouldSuppressMessage(args[0])) return;
    (originalCount as any).apply(console, args);
  };

  console.assert = function(...args: any[]) {
    if (args.length > 0 && shouldSuppressMessage(args[0])) return;
    (originalAssert as any).apply(console, args);
  };

  // Suppress XMLHttpRequest logging at the browser level
  if (typeof XMLHttpRequest !== 'undefined') {
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;
    
    XMLHttpRequest.prototype.open = function(method: string, url: string | URL, async: boolean = true, user?: string | null, password?: string | null) {
      return originalXHROpen.call(this, method, url, async, user, password);
    };
    
    XMLHttpRequest.prototype.send = function(data?: Document | XMLHttpRequestBodyInit | null) {
      return originalXHRSend.call(this, data);
    };
  }

  // Suppress fetch API logging
  if (typeof window !== 'undefined' && window.fetch) {
    const originalFetch = window.fetch;
    window.fetch = function(input: RequestInfo | URL, init?: RequestInit) {
      return originalFetch.call(this, input, init);
    };
  }

  // Suppress Angular development mode warning
  if (typeof window !== 'undefined') {
    const originalAddEventListener = window.addEventListener;
    window.addEventListener = function(this: Window, type: string, listener: EventListener | EventListenerObject, options?: boolean | AddEventListenerOptions) {
      if (type === 'error' || type === 'unhandledrejection') {
        const wrappedListener = function(this: any, event: Event) {
          if (event instanceof ErrorEvent) {
            const message = event.message || '';
            if (shouldSuppressMessage(message)) return;
          }
          if (typeof listener === 'function') {
            listener.call(this, event);
          } else if (listener && typeof listener.handleEvent === 'function') {
            listener.handleEvent(event);
          }
        };
        return originalAddEventListener.call(this, type, wrappedListener as EventListener, options);
      }
      return originalAddEventListener.call(this, type, listener, options);
    };
  }

})();

bootstrapApplication(AppComponent, appConfig)
